import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AccountMembership, APIService, CreateTeamAccountInput, UpdateAccountInput } from '../../../../API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { AmplifyStorageService } from '../../../../../services/amplify-storage.service';
import { UppyComponent } from '../../../uppy/uppy.component';
import { AiWorkspaceSetupService } from '../ai-workspace-setup.service';
import { Subject, Subscription } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { AuthService, WORKSPACE_ACCESS_PENDING_TOAST } from '../../../../auth/auth.service';
import { CountryISO, PhoneNumberFormat, SearchCountryField, NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { capitalize } from 'globals/functions';
import { isValidGstin } from '../../gstin';

@Component({
  selector: 'ws-account-basics',
  templateUrl: './ws-account-basics.component.html',
  imports: [FormsModule, ReactiveFormsModule, NgClass, NgTemplateOutlet, MatButton, UppyComponent, NgxIntlTelInputModule]
})
export class WsAccountBasicsComponent implements OnInit, OnDestroy {
  @Input() activeAccount: AccountMembership;
  @Input() testMode = false;
  @Input() settingUpTrialAccount = false;
  @Input() prefillWebsiteUrl = '';
  @Output() completed = new EventEmitter<{ accountId: string; businessDescription: string; extractedData?: any; trialUserId?: string; website?: string; detectedLogoUrl?: string; gstin?: string }>();

  @ViewChild(UppyComponent, { static: false }) uppyComponent: UppyComponent;

  // Flow mode
  mode: 'choose' | 'website' | 'manual' = 'choose';

  // Website mode
  websiteUrl = '';
  additionalUrls: string[] = [''];
  isExtracting = false;
  extractedData: any = null;
  extractionMessage = '';
  private extractionInterval: any;

  // Manual mode fields
  businessName = '';
  businessDescription = '';
  gstin = '';

  // Trial account signup form
  signUpForm = new UntypedFormGroup({
    sms: new UntypedFormControl(undefined, [Validators.required]),
    fullName: new UntypedFormControl(undefined, [Validators.required]),
    email: new UntypedFormControl(undefined, [Validators.required, Validators.email]),
  });
  separateDialCode = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.India, CountryISO.UnitedStates];

  // Shared
  isSaving = false;
  registeredEmail = '';
  detectedLogoUrl: string = null; // Fallback when CORS blocks blob download for Uppy
  subject: Subject<[string, any, any, any]> = new Subject();
  private subjectSubscription?: Subscription;

  constructor(
    private api: APIService,
    private toastr: ToastrService,
    private storageService: AmplifyStorageService,
    private aiService: AiWorkspaceSetupService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.subjectSubscription = this.subject.subscribe(ev => this.onUppyEvent(ev));

    // Pre-fill website from marketing site redirect
    if (this.prefillWebsiteUrl) {
      this.websiteUrl = this.prefillWebsiteUrl;
      this.mode = 'website';
      setTimeout(() => this.extractFromWebsite(), 800);
    }

    // Fetch user email directly (this route is outside DefaultLayout so dataService.getRegisteredUser() won't have data)
    this.auth.currentAuthenticatedUser().then(async (cognitoUser) => {
      const userId = cognitoUser.getSignInUserSession()?.getIdToken()?.payload?.userId;
      if (userId) {
        const user = await this.api.GetUser(userId);
        if (user?.registeredEmail) {
          this.registeredEmail = user.registeredEmail;
        }
      }
      // Fallback: get email from Cognito attributes
      if (!this.registeredEmail) {
        const attrs = await this.auth.getUserDetails();
        const emailAttr = attrs?.find(a => a.getName() === 'email');
        if (emailAttr) this.registeredEmail = emailAttr.getValue();
      }
    }).catch(() => {});

  }

  ngOnDestroy(): void {
    this.subjectSubscription?.unsubscribe();
    this.subject.complete();
    this.stopExtractionAnimation();
  }

  ngAfterViewInit() {
    if (this.uppyComponent) {
      this.uppyComponent.getUppyInstance().setOptions({
        autoProceed: false,
        restrictions: { maxNumberOfFiles: 1, allowedFileTypes: ['image/*'] },
      });
    }
  }

  getUppyPlugins() {
    return [
      ['Dashboard', { target: 'body', inline: true, hideUploadButton: true }],
    ];
  }

  async onUppyEvent(_ev) {
    // No-op — just accept files as-is, no editor
  }

  selectMode(mode: 'website' | 'manual') {
    this.mode = mode;
  }

  addUrlField() {
    this.additionalUrls.push('');
  }

  removeUrlField(index: number) {
    this.additionalUrls.splice(index, 1);
  }

  trackByIndex(index: number) {
    return index;
  }

  private readonly EXTRACTION_MESSAGES = [
    'Searching for your website...',
    'Reading your business pages...',
    'Identifying products & services...',
    'Extracting business details...',
    'Analyzing your business model...',
    'Almost there, please wait...',
  ];

  async extractFromWebsite() {
    if (!this.websiteUrl.trim()) {
      this.toastr.warning('Please enter a website URL');
      return;
    }
    this.websiteUrl = this.normalizeUrl(this.websiteUrl);
    this.isExtracting = true;
    this.startExtractionAnimation();

    try {
      const extras = this.additionalUrls.filter(u => u?.trim()).map(u => this.normalizeUrl(u));
      this.extractedData = await this.aiService.extractBusinessFromWebsite(
        this.websiteUrl, extras
      );
      // Pre-fill from extracted data
      this.businessName = this.extractedData.businessName || '';
      this.businessDescription = this.extractedData.businessDescription || '';
      this.toastr.success('Business information extracted');

      // If AI didn't find a logo, try fallback sources
      if (!this.extractedData.logoUrl && this.websiteUrl.trim()) {
        this.extractedData.logoUrl = this.getFallbackLogoUrl(this.websiteUrl.trim());
      }

      // Download logo and add to Uppy (after view updates to render uppy)
      if (this.extractedData.logoUrl) {
        setTimeout(() => this.downloadAndSetLogo(this.extractedData.logoUrl), 500);
      }
    } catch (e: any) {
      console.error('Error extracting business info:', e);
      this.toastr.error(e.message || 'Failed to extract info. Please try manually.');
      // Fall back to manual
      this.mode = 'manual';
    } finally {
      this.isExtracting = false;
      this.stopExtractionAnimation();
    }
  }

  private startExtractionAnimation() {
    let index = 0;
    this.extractionMessage = this.EXTRACTION_MESSAGES[0];
    this.extractionInterval = setInterval(() => {
      index = (index + 1) % this.EXTRACTION_MESSAGES.length;
      this.extractionMessage = this.EXTRACTION_MESSAGES[index];
    }, 3000);
  }

  private stopExtractionAnimation() {
    if (this.extractionInterval) {
      clearInterval(this.extractionInterval);
      this.extractionInterval = null;
    }
    this.extractionMessage = '';
  }

  private getFallbackLogoUrl(websiteUrl: string): string | null {
    try {
      const url = new URL(websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`);
      const domain = url.hostname;
      // Use Google's high-res favicon service as fallback
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    } catch {
      return null;
    }
  }

  private async downloadAndSetLogo(url: string) {
    // Resolve the actual image URL (handles _next/image proxy, etc.)
    const resolvedUrl = this.resolveImageUrl(url);
    console.log('[WS-LOGO] Original url:', url);
    console.log('[WS-LOGO] Resolved url:', resolvedUrl);

    // Store the resolved URL as fallback — will be saved directly to businessLogoUrl
    // if Uppy ends up empty (most external sites block CORS so blob download fails)
    this.detectedLogoUrl = resolvedUrl;

    if (!this.uppyComponent) return;

    // Try each URL (resolved first, then original if different)
    const urlsToTry = resolvedUrl !== url ? [resolvedUrl, url] : [url];

    for (const tryUrl of urlsToTry) {
      const blob = await this.fetchImageBlob(tryUrl);
      if (blob && blob.size > 1024) { // Must be > 1KB to be a real image
        const fileName = tryUrl.split('/').pop()?.split('?')[0] || 'logo.png';
        this.uppyComponent.getUppyInstance().addFile({
          name: fileName,
          type: blob.type,
          data: blob,
          source: 'remote-url',
        });
        console.log('[WS-LOGO] Added to Uppy: size=', blob.size, 'type=', blob.type, 'from:', tryUrl);
        return;
      } else if (blob) {
        console.log('[WS-LOGO] Skipping tiny image (', blob.size, 'bytes) from:', tryUrl);
      }
    }

    // CORS blocked — that's OK, we'll use detectedLogoUrl directly when saving
    console.log('[WS-LOGO] CORS blocked blob download — will save URL directly as businessLogoUrl');
  }

  /** Extract the real image URL from proxy/optimizer URLs (e.g. Next.js _next/image) */
  private resolveImageUrl(url: string): string {
    try {
      const parsed = new URL(url);

      // Next.js _next/image proxy: extract the `url` query param
      if (parsed.pathname.includes('_next/image') && parsed.searchParams.has('url')) {
        const innerPath = parsed.searchParams.get('url');
        // Inner path can be relative (/_next/static/...) or absolute
        if (innerPath.startsWith('http')) return innerPath;
        return `${parsed.origin}${innerPath}`;
      }
    } catch { /* not a valid URL, return as-is */ }
    return url;
  }

  /** Fetch an image URL and return a blob, trying CORS fetch then canvas fallback */
  private async fetchImageBlob(url: string): Promise<Blob | null> {
    // Try fetch first (works if server supports CORS)
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (response.ok) {
        const blob = await response.blob();
        if (blob.size > 0 && blob.type.startsWith('image/')) {
          return blob;
        }
      }
    } catch { /* CORS blocked — try canvas fallback */ }

    // Canvas fallback
    try {
      return await this.loadImageAsBlob(url);
    } catch {
      return null;
    }
  }

  private loadImageAsBlob(url: string): Promise<Blob | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => resolve(blob), 'image/png');
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  /** Ensure URL has a protocol — backend rejects bare domains like "syncpedia.in" */
  private normalizeUrl(url: string): string {
    if (!url) return url;
    url = url.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    return url;
  }

  /** GSTIN is optional; when entered it must be a valid 15-character GSTIN. */
  get gstinInvalid(): boolean {
    return !!this.gstin?.trim() && !isValidGstin(this.gstin);
  }

  get canSubmit(): boolean {
    const baseValid = !!this.businessName?.trim() && !!this.businessDescription?.trim() && !this.gstinInvalid && !this.isSaving;
    if (this.settingUpTrialAccount) {
      return baseValid && this.signUpForm.valid;
    }
    return baseValid;
  }

  async onSubmit() {
    if (!this.canSubmit || this.isSaving) return;
    this.isSaving = true;

    const accountName = this.businessName.trim();
    const description = this.businessDescription.trim();
    const website = this.websiteUrl?.trim() ? this.normalizeUrl(this.websiteUrl) : undefined;
    const gstin = this.gstin?.trim() ? this.gstin.trim().toUpperCase() : undefined;

    if (this.testMode) {
      this.toastr.success('Test mode: Organization created');
      this.isSaving = false;
      this.completed.emit({ accountId: this.activeAccount?.accountId || 'test-account-id', businessDescription: description, extractedData: this.extractedData });
      return;
    }

    try {
      // For trial accounts: create the prospect user first
      let createdById: string = null;
      let trialEmail: string = null;

      if (this.settingUpTrialAccount) {
        const signUpResult = await this.createTrialUser();
        createdById = signUpResult.userId;
        trialEmail = signUpResult.email;
      }

      // In trial mode use the prospect's email; otherwise use the logged-in user's email
      const email = trialEmail || this.registeredEmail || '';
      const input = <CreateTeamAccountInput>{};
      input.accountInput = {
        accountName: accountName,
        industry: this.extractedData?.industry || 'generic',
        billingEmail: email || undefined,
        website: website,
      } as any;
      if (this.settingUpTrialAccount && createdById) {
        (input.accountInput as any).createdById = createdById;
      }
      input.isTrialAccount = true; // Always trial — moved to paid later
      input.dealMode = 'manual';
      input.mapRegisteredMobileNumberToAccount = true;
      input.mapSecondaryMobileNumberToAccount = false;
      input.accountEmail = email;

      const account = await this.api.CreateTeamAccount(input);
      console.log('Team Account Created:', account);

      if (!this.settingUpTrialAccount) {
        // The session token was minted before this membership existed, so the
        // backend would deny account-scoped calls for the new account. Wait for
        // the claim before the updates below and before later steps touch it.
        const access = await this.auth.awaitAccountInToken(account.id);
        if (access.cancelled) {
          // The user signed out mid-flow; there is no session to continue with.
          return;
        }
        if (!access.inToken) {
          this.toastr.warning(WORKSPACE_ACCESS_PENDING_TOAST.message, WORKSPACE_ACCESS_PENDING_TOAST.title);
        }
        // Non-trial: current user owns the account, can update immediately
        const updateInput = <UpdateAccountInput>{};
        updateInput.id = account.id;
        updateInput.webPreferences = [
          { key: 'businessDescription', value: description },
          ...(website ? [{ key: 'businessWebsite', value: website }] : []),
        ];
        if (gstin) {
          updateInput.taxId = gstin;
          updateInput.taxIdType = 'GST';
        }
        await this.api.UpdateAccount(updateInput).catch(e => console.error('Error saving preferences:', e));
        await this.uploadProfilePic(account.id);
      }
      // Trial mode: Salesmax user isn't a member of the new account yet,
      // so UpdateAccount would fail. Pass data via event — orchestrator
      // will update after adding Salesmax user to the trial account.

      this.toastr.success(this.settingUpTrialAccount ? 'Trial account created' : 'Organization created successfully');
      this.completed.emit({
        accountId: account.id,
        businessDescription: description,
        extractedData: this.extractedData,
        trialUserId: createdById,
        website,
        detectedLogoUrl: this.settingUpTrialAccount ? this.detectedLogoUrl : undefined,
        gstin,
      });
    } catch (e: any) {
      console.error('Error creating/updating account:', e);
      const msg = e?.errors?.[0]?.message || e?.message || 'Something went wrong';
      this.toastr.error(msg, 'Error');
    } finally {
      this.isSaving = false;
    }
  }

  /** For trial mode: sign up the prospect user and return their userId */
  private async createTrialUser(): Promise<{ userId: string; email: string }> {
    const name = capitalize(this.signUpForm.controls['fullName'].value);
    const e164Number = this.signUpForm.controls['sms'].value.e164Number;
    const email = this.signUpForm.controls['email'].value;

    this.toastr.success('Creating new user for trial account');

    try {
      await this.auth.signUp(e164Number, name, email, '12345', 'superadmin');
    } catch (e: any) {
      // Amplify v6 uses name; older auth errors use code. Reuse only an existing user.
      if (e?.name !== 'UsernameExistsException' && e?.code !== 'UsernameExistsException') throw e;
    }

    // Wait for user record to be created in DB
    await new Promise(resolve => setTimeout(resolve, 2000));

    const result = await this.api.GetUserByNumber(e164Number);
    if (result?.items?.length > 0) {
      return { userId: result.items[0].id, email };
    }
    throw new Error('Could not find newly created user');
  }

  private async uploadProfilePic(accountId: string) {
    const files = this.uppyComponent?.getUppyInstance()?.getFiles();
    if (files && files.length === 1) {
      // User uploaded or CORS-friendly blob was added to Uppy — upload to S3
      const file = files[0];
      const name = `profile-picture-${accountId}-${file.name}`;
      this.storageService.uploadFileToS3(file.data, name, 'public', file.type);
      const updateInput = <UpdateAccountInput>{};
      updateInput.id = accountId;
      updateInput.businessLogoUrl = name;
      await this.api.UpdateAccount(updateInput);
    } else if (this.detectedLogoUrl) {
      // CORS blocked blob download — save the external URL directly
      // ProfileImageComponent already handles http URLs natively
      const updateInput = <UpdateAccountInput>{};
      updateInput.id = accountId;
      updateInput.businessLogoUrl = this.detectedLogoUrl;
      await this.api.UpdateAccount(updateInput);
      console.log('[WS-LOGO] Saved external URL directly as businessLogoUrl:', this.detectedLogoUrl);
    }
  }
}
